/**
 * AttributSchemaTab — the Regelwerk Attribut-Schema (P2 Placement Slice 3).
 *
 * The atomic field schema grouped by Ebene + Sparte / Kundentyp. Slice 3 is the LIMITED PATCH: the
 * DE label (inline), Pflicht + Aktiv (toggles) and Zuruecksetzen. The field typ/options + creating
 * new fields are Slice 3b. All write controls gated on `canManage` (the api is the hard line).
 */

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { igb2bCodeLabel, type RegelwerkAttributSchemaItem } from '@brokly/shared';
import { apiClient } from '../../../../lib/api-client';
import { useRegelwerkAttributSchema } from '../../hooks';
import { Button, LoadingState, ErrorState, EmptyState, TextInput, Toggle } from '../../components';

export function AttributSchemaTab({ canManage }: { canManage: boolean }) {
  const [ebene, setEbene] = useState<string>('');
  const filters = useMemo(() => ({ ebene: ebene || undefined, include_inactive: true }), [ebene]);
  const { data, isLoading, isError } = useRegelwerkAttributSchema(filters);

  // Group rows by their slice (ebene + branchencode | profil_typ) for a readable list.
  const groups = useMemo(() => {
    const map = new Map<string, { label: string; items: RegelwerkAttributSchemaItem[] }>();
    for (const item of data?.items ?? []) {
      const key = item.ebene === 'police' ? `police:${item.branchencode}` : `kunde:${item.profil_typ}`;
      const label =
        item.ebene === 'police'
          ? `Police · ${item.branchencode} ${igb2bCodeLabel(item.branchencode ?? '')}`
          : `Kunde · ${item.profil_typ === 'firma' ? 'Firma' : 'Privatperson'}`;
      if (!map.has(key)) map.set(key, { label, items: [] });
      map.get(key)!.items.push(item);
    }
    return [...map.values()];
  }, [data]);

  return (
    <div data-testid="regelwerk-schema-tab" className="space-y-4">
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Ebene
        <select
          value={ebene}
          onChange={(e) => setEbene(e.target.value)}
          aria-label="Ebene filtern"
          className="w-40 rounded-md border border-border bg-background px-2 py-1 text-sm"
        >
          <option value="">Alle</option>
          <option value="police">Police</option>
          <option value="kunde">Kunde</option>
        </select>
      </label>

      {isLoading ? (
        <LoadingState />
      ) : isError || data == null ? (
        <ErrorState />
      ) : groups.length === 0 ? (
        <EmptyState label="Kein Schema fuer diese Filter." />
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.label} className="rounded-lg border border-border">
              <div className="border-b border-border bg-muted/40 px-3 py-2 text-xs font-semibold text-muted-foreground">
                {g.label}
              </div>
              <div className="divide-y divide-border">
                {g.items.map((item) => (
                  <SchemaRow key={item.id} item={item} canManage={canManage} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SchemaRow({ item, canManage }: { item: RegelwerkAttributSchemaItem; canManage: boolean }) {
  const qc = useQueryClient();
  const [label, setLabel] = useState(item.label_i18n.de);
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['admin', 'regelwerk', 'attribut-schema'] });

  const patch = useMutation({
    mutationFn: (body: Parameters<typeof apiClient.admin.patchRegelwerkSchemaField>[1]) =>
      apiClient.admin.patchRegelwerkSchemaField(item.id, { ...body, expected_updated_at: item.updated_at }),
    onSuccess: invalidate,
  });
  const reset = useMutation({
    mutationFn: () => apiClient.admin.resetRegelwerkSchemaField(item.id),
    onSuccess: invalidate,
  });

  return (
    <div data-testid={`regelwerk-schema-row-${item.feld_key}`} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
      <code className="min-w-40 text-xs text-muted-foreground">{item.feld_key}</code>
      <span className="text-xs text-muted-foreground">{item.typ}</span>
      <div className="flex-1 min-w-48">
        {canManage ? (
          <TextInput value={label} onChange={(e) => setLabel(e.target.value)} aria-label={`Bezeichnung ${item.feld_key}`} />
        ) : (
          <span>{item.label}</span>
        )}
      </div>
      {canManage && (
        <>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Pflicht
            <Toggle checked={item.pflicht} onChange={(v) => patch.mutate({ pflicht: v })} label={`Pflicht ${item.feld_key}`} />
          </label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Aktiv
            <Toggle checked={item.aktiv} onChange={(v) => patch.mutate({ aktiv: v })} label={`Aktiv ${item.feld_key}`} />
          </label>
          <Button
            size="sm"
            variant="outline"
            disabled={patch.isPending || label === item.label_i18n.de}
            onClick={() => patch.mutate({ label_i18n: { ...item.label_i18n, de: label } })}
          >
            Speichern
          </Button>
          {item.origin === 'system' && (
            <Button size="sm" variant="ghost" disabled={reset.isPending} onClick={() => reset.mutate()}>
              Zuruecksetzen
            </Button>
          )}
        </>
      )}
    </div>
  );
}
